import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Input, Button, Alert, Typography, Space, Divider } from 'antd';
import { LoginHeadler } from '../../../functions/auth';
import styles from './styles/Login.module.css';

const { Title, Text } = Typography;

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [serverMsg, setServerMsg] = useState(null);
  const [errors, setErrors] = useState({});

  const initial = useMemo(() => ({
    village: '',
    phase: '',
    password: '',
  }), []);

  const [form, setForm] = useState(initial);

  useEffect(() => {
    // ไม่ต้องตรวจสอบ token ที่นี่ เพราะ PublicRoute จะจัดการแล้ว
    // ถ้ามี token ที่ยังไม่หมดอายุ จะถูก redirect ไป /memberdata โดย PublicRoute
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validate = () => {
    const newErr = {};
    const vVillage = (form.village || '').trim();
    const vPhase = (form.phase || '').trim();
    const vPassword = (form.password || '').trim();

    if (!vVillage) newErr.village = 'กรุณาใส่ชื่อหมู่บ้าน';
    else if (vVillage.length > 100) newErr.village = 'ยาวเกินกำหนด (ไม่เกิน 100 ตัวอักษร)';
    else if (/\s/.test(vVillage)) newErr.village = 'ชื่อหมู่บ้านต้องไม่มีช่องว่าง';

    if (!vPhase) newErr.phase = 'กรุณาระบุเฟสหมู่บ้าน!';
    else {
      const num = Number(vPhase);
      if (!Number.isInteger(num) || num <= 0 || num > 100) newErr.phase = 'กรุณาใส่จำนวนเต็มที่มากกว่า 0 และไม่เกิน 100';
    }

    if (!vPassword) newErr.password = 'กรุณาใส่รหัสผ่านของคุณ!';
    else if (vPassword.length < 6) newErr.password = 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร';
    else if (/\s/.test(vPassword)) newErr.password = 'รหัสผ่านต้องไม่มีช่องว่าง';

    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const redirectByRole = (role) => {
    switch (role) {
      case 'admin':
        navigate('/memberdata', { replace: true });
        break;
      default:
        navigate('/', { replace: true });
        break;
    }
  };

  const onSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setServerMsg(null);
    if (loading) return;

    // ตรวจสอบค่าก่อนส่ง
    const trimmedForm = {
      village: (form.village || '').trim(),
      phase: (form.phase || '').trim(),
      password: (form.password || '').trim(),
    };

    // ตรวจสอบว่ามีช่องว่างหรือไม่
    if (/\s/.test(trimmedForm.village) || /\s/.test(trimmedForm.password)) {
      setServerMsg({ type: 'error', text: 'ข้อมูลต้องไม่มีช่องว่าง กรุณาตรวจสอบและลองอีกครั้ง' });
      return;
    }

    if (!validate()) return;

    const payload = {
      village: trimmedForm.village,
      phase: Number(trimmedForm.phase),
      password: trimmedForm.password,
    };

    setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 วินาที timeout

    try {
      const res = await LoginHeadler(payload, { signal: controller.signal });
      clearTimeout(timeoutId);

      const { id, village, role, phase } = res?.data?.payload || {};
      const { token } = res?.data || {};

      if (!token || !id || !village || !role) {
        throw new Error('ข้อมูลการเข้าสู่ระบบไม่สมบูรณ์ กรุณาลองอีกครั้ง');
      }

      // ตรวจสอบ token format
      if (!token.includes('.') || token.split('.').length !== 3) {
        throw new Error('รูปแบบ token ไม่ถูกต้อง');
      }

      dispatch({
        type: 'LOGGED_IN_USER',
        payload: { id, village, role, token, phase },
      });

      localStorage.setItem('token', token);

      setServerMsg({ type: 'success', text: 'เข้าสู่ระบบสำเร็จ!' });
      redirectByRole(role);
    } catch (err) {
      clearTimeout(timeoutId);
      localStorage.removeItem('token');

      let errorMessage = 'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง!';

      if (err.name === 'AbortError') {
        errorMessage = 'การเชื่อมต่อใช้เวลานานเกินไป กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
      } else if (err.response) {
        // Server responded with error status
        const status = err.response.status;
        const serverMsg = err.response.data?.message;

        if (status === 400) {
          errorMessage = serverMsg || 'ข้อมูลเข้าสู่ระบบไม่ถูกต้อง';
        } else if (status === 401) {
          errorMessage = 'รหัสผ่านไม่ถูกต้อง';
        } else if (status === 404) {
          errorMessage = 'ไม่พบข้อมูลหมู่บ้านนี้';
        } else if (status >= 500) {
          errorMessage = 'เซิร์ฟเวอร์มีปัญหา กรุณาลองอีกครั้งในภายหลัง';
        } else {
          errorMessage = serverMsg || `เกิดข้อผิดพลาด (${status})`;
        }
      } else if (err.request) {
        // Network error
        errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
      }

      console.error('Login error:', err);
      setServerMsg({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${styles.page} page-background page-centered`}>
      <Card className={styles.card} bordered={false}>
        <header className={styles.header}>
          <img src="/logoTackXpert.png" alt="TackXpert Logo" className={styles.logo} />
          <Title level={2} className={`${styles.title} page-title`}>TRACXPERT</Title>
          <Text type="secondary" className={`${styles.subtitle} page-subtitle`}>ระบบจัดการทะเบียนรถหมู่บ้าน</Text>
        </header>

        <Divider className={styles.divider} />

        <form onSubmit={onSubmit} noValidate className={styles.form}>
          <Space direction="vertical" size="middle" className={styles.stack}>

            <div className={styles.group}>
              <label htmlFor="village" className={styles.label}>ชื่อหมู่บ้าน</label>
              <Input
                id="village"
                name="village"
                type="text"
                placeholder="เช่น villageA, villageB"
                value={form.village}
                onChange={handleChange}
                aria-invalid={!!errors.village}
                aria-describedby="village-err"
                autoComplete="organization"
                size="large"
              />
              {errors.village && <div id="village-err" className={styles.error}>{errors.village}</div>}
            </div>

            <div className={styles.group}>
              <label htmlFor="phase" className={styles.label}>เฟส</label>
              <Input
                id="phase"
                name="phase"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                placeholder="เช่น 1, 2, 3"
                value={form.phase}
                onChange={handleChange}
                aria-invalid={!!errors.phase}
                aria-describedby="phase-err"
                size="large"
              />
              {errors.phase && <div id="phase-err" className={styles.error}>{errors.phase}</div>}
            </div>

            <div className={styles.group}>
              <label htmlFor="password" className={styles.label}>รหัสผ่าน</label>
              <Input.Password
                id="password"
                name="password"
                placeholder="กรอกรหัสผ่านของคุณ"
                value={form.password}
                onChange={handleChange}
                aria-invalid={!!errors.password}
                aria-describedby="password-err"
                autoComplete="current-password"
                size="large"
              />
              {errors.password && <div id="password-err" className={styles.error}>{errors.password}</div>}
            </div>

            {/* Auto-login removed */}

            {serverMsg && (
              <Alert
                type={serverMsg.type === 'error' ? 'error' : 'success'}
                message={serverMsg.text}
                showIcon
                className={styles.alert}
              />
            )}

            <div className={styles.actions}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                disabled={loading}
                loading={loading}
                className={styles.loginButton}
              >
                {loading ? 'กำลังเข้าสู่ระบบ…' : 'เข้าสู่ระบบ'}
              </Button>
              <p className={styles.helper}>ข้อมูลของคุณถูกส่งผ่านการเชื่อมต่อที่ปลอดภัย</p>
            </div>

            <div className={styles.register}>
              <Text>ยังไม่มีบัญชี? <Link to="/register">ลงทะเบียนที่นี่</Link></Text>
            </div>
          </Space>
        </form>
      </Card>

      <footer className="page-footer">
        <span>© {new Date().getFullYear()} TRACXPERT</span>
        <span className="dot">•</span>
        <a href="/help" rel="noreferrer">ช่วยเหลือ</a>
      </footer>
    </div>
  );
}   
